import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { TestletType } from '../constants/testlet-type.enum';
import { PublicRoutePathParameters, RoutePathParameters } from '../models/assessment-models';

@Injectable({
  providedIn: 'root',
})
export class PageUtils {
  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  public navigateToErrorPage(errorMessage: string) {
    this.router.navigate(['/error'], {
      state: { error: errorMessage },
    });
  }

  public navigateToIntroductionPage(manualReload: boolean) {
    if (!manualReload) {
      return;
    }

    const url = this.buildParameterisedRoute('introduction');
    this.router.navigateByUrl(url);
  }

  public getNextPage(testName: TestletType | string): string {
    switch (testName) {
      case TestletType.Symbols:
      case 'Symbols': {
        return '/symbols';
      }
      case TestletType.SimilarWords:
      case 'Similar Words': {
        return '/similar-words';
      }
      case TestletType.NumberRecall:
      case 'Number Recall': {
        return '/number-recall';
      }
      case TestletType.PictureCompletion:
      case 'Picture Completion': {
        return '/picture-completion';
      }
      case TestletType.FakeWords:
      case 'Fake Words': {
        return '/fake-words';
      }
      case TestletType.MissingNumbers:
      case 'Missing Numbers': {
        return '/missing-numbers';
      }
      case TestletType.Objects:
      case 'Objects': {
        return 'objects';
      }
      case TestletType.ShapesAndColors:
      case 'Shapes & Colours': {
        return '/shapes-and-colours';
      }
      default: {
        return '/symbols';
      }
    }
  }

  public getTestletByName(name: string): TestletType {
    switch (name) {
      case 'Symbols': {
        return TestletType.Symbols;
      }
      case 'SimilarWord':
      case 'Similar Words': {
        return TestletType.SimilarWords;
      }
      case 'NumberRecall':
      case 'Number Recall': {
        return TestletType.NumberRecall;
      }
      case 'PictureCompletion':
      case 'Picture Completion': {
        return TestletType.PictureCompletion;
      }
      case 'FakeWord':
      case 'Fake Words': {
        return TestletType.FakeWords;
      }
      case 'MissingNumber':
      case 'Missing Numbers': {
        return TestletType.MissingNumbers;
      }
      case 'Objects': {
        return TestletType.Objects;
      }
      case 'ShapesAndColours':
      case 'Shapes & Colours': {
        return TestletType.ShapesAndColors;
      }
      default: {
        return TestletType.Symbols;
      }
    }
  }

  public buildParameterisedRoute(page: string): string {
    const routeParams = this.getRoutePathParams(this.route.snapshot);
    const orgCode = routeParams.orgCode;
    const campaignCode = routeParams.campaignCode;
    const invitationCode = routeParams.invitationCode;

    const trimmedPage = page.replace('/', '');

    return `${orgCode}/${campaignCode}/${invitationCode}/${trimmedPage}`;
  }

  public buildParameterisedContentRoute(page: string): string {
    const routeParams = this.getRoutePathParams(this.route.snapshot);
    const orgCode = routeParams.orgCode;
    const campaignCode = routeParams.campaignCode;

    const trimmedPage = page.replace('/', '');

    return `${orgCode}/${campaignCode}/${trimmedPage}`;
  }

  private getRoutePathParams(routeSnapshot: ActivatedRouteSnapshot): RoutePathParameters {
    const urlSegments = routeSnapshot.firstChild?.pathFromRoot.map((s) => s.url).reduce((a, e) => a.concat(e));

    if (urlSegments == null) {
      throw new Error('URL improperly formed');
    }

    const orgCode = urlSegments[0].path;
    const campaignCode = urlSegments[1].path;
    const invitationCode = urlSegments[2].path;

    return { orgCode, campaignCode, invitationCode };
  }

  public getRoutePathParameters(routeSnapshot: ActivatedRouteSnapshot): RoutePathParameters {
    const orgCode = routeSnapshot.url[0].path;
    const campaignCode = routeSnapshot.url[1].path;
    const invitationCode = routeSnapshot.url[2].path;

    return { orgCode, campaignCode, invitationCode };
  }

  public getLandingRoutePathParameters(routeSnapshot: ActivatedRouteSnapshot): PublicRoutePathParameters {
    const orgCode = routeSnapshot.url[0].path;
    const campaignCode = routeSnapshot.url[1].path;

    return { orgCode, campaignCode };
  }
}
